// Package weight input — manual entry as the always-available default,
// with a "Read from scale" button that uses WebHID to talk to USB POS scales
// (DYMO M25, Mettler Toledo PS60, etc.). The HID Point of Sale Scale spec
// is usagePage 0x8d. No drivers required.
//
// The component owns its display state (oz value, lb+oz toggle) and reports
// the rounded weight in OUNCES through onChange(weightOz, source).
// `source` is 'manual' or 'hid' depending on where the reading came from.
//
// Graceful fallback: if WebHID is unavailable (Safari, Firefox, non-HTTPS),
// the button is disabled with an explanatory tooltip — manual entry still works.

import { useEffect, useRef, useState } from 'react';

export default function ScaleReader({ value, source, onChange }) {
  // Display state. Internal value is always ounces (a non-negative number).
  const [unit, setUnit]   = useState('oz');                 // 'oz' | 'lb'
  const [text, setText]   = useState(value ? String(value) : '');
  const [hidStatus, setHidStatus] = useState(null);         // 'idle'|'reading'|'error'|'unsupported'
  const [hidError, setHidError]   = useState(null);
  const deviceRef = useRef(null);

  const supportsHid = typeof navigator !== 'undefined' && !!navigator.hid;

  useEffect(() => {
    setText(value != null ? String(unit === 'oz' ? value : (value / 16).toFixed(2)) : '');
  }, [value, unit]);

  function commitText(v, src = 'manual') {
    setText(v);
    const num = Number(v);
    if (!Number.isFinite(num) || num <= 0) { onChange?.(null, src); return; }
    const oz = unit === 'oz' ? num : num * 16;
    onChange?.(Math.round(oz * 100) / 100, src);
  }

  async function readFromScale() {
    if (!supportsHid) { setHidStatus('unsupported'); return; }
    setHidError(null); setHidStatus('reading');
    try {
      // HID POS Scale: usage page 0x8d, usage 0x32. Most cheap USB scales
      // also expose a generic vendor-defined page; we let the user pick if
      // the filter doesn't catch it.
      const filters = [
        { usagePage: 0x8d, usage: 0x32 },                 // POS Scale
        { vendorId: 0x0922 /* DYMO */ },
        { vendorId: 0x0eb8 /* Mettler Toledo */ },
        { vendorId: 0x067b /* Prolific */ },
      ];
      const [device] = await navigator.hid.requestDevice({ filters });
      if (!device) { setHidStatus('idle'); return; }
      if (!device.opened) await device.open();
      deviceRef.current = device;

      // Read one stable weight report, with a 5s timeout
      const timeout = setTimeout(() => {
        setHidError('Timed out — make sure something is on the scale.');
        setHidStatus('error');
        device.removeEventListener('inputreport', handler);
      }, 5000);

      const handler = (event) => {
        // POS Scale spec: Report ID byte 0; status byte 1; weight unit byte 2;
        // weight LE bytes 3-4; exponent (signed) byte 5.
        const d = new DataView(event.data.buffer);
        if (d.byteLength < 5) return;
        const status   = d.getUint8(0);  // 2 = 'weight stable'
        const unitCode = d.getUint8(1);  // 11 = ounce, 12 = pound
        const raw      = d.getUint16(2, true);
        const exp      = d.getInt8(4);   // typically 0 or -1
        if (status !== 2 /* stable */) return;             // wait for stability
        const scaled = raw * Math.pow(10, exp);
        const oz = unitCode === 12 ? scaled * 16 : scaled; // lb -> oz, else assume oz
        clearTimeout(timeout);
        device.removeEventListener('inputreport', handler);
        setHidStatus('idle');
        commitText(unit === 'oz' ? String(oz) : String((oz / 16).toFixed(2)), 'hid');
      };
      device.addEventListener('inputreport', handler);
    } catch (e) {
      setHidError(e.message || 'Could not read from scale');
      setHidStatus('error');
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          className="input font-mono w-32"
          placeholder="0.00"
          value={text}
          onChange={(e) => commitText(e.target.value, 'manual')}
        />
        <select
          className="input w-20"
          value={unit}
          onChange={(e) => {
            const next = e.target.value;
            const num = Number(text);
            if (Number.isFinite(num) && num > 0 && next !== unit) {
              const oz = unit === 'oz' ? num : num * 16;
              setText(next === 'oz' ? String(oz) : String((oz / 16).toFixed(2)));
            }
            setUnit(next);
          }}
        >
          <option value="oz">oz</option>
          <option value="lb">lb</option>
        </select>
        <button
          type="button"
          className="btn-ghost"
          disabled={!supportsHid || hidStatus === 'reading'}
          onClick={readFromScale}
          title={supportsHid
            ? 'Reads weight from a connected USB POS scale via WebHID.'
            : 'WebHID not available in this browser. Use Chrome or Edge over HTTPS.'}
        >
          {hidStatus === 'reading' ? 'Reading…' : 'Read from scale'}
        </button>
        {source === 'hid' && (
          <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">scale</span>
        )}
      </div>
      {hidStatus === 'error' && hidError && (
        <div className="text-xs text-amber-700">Scale: {hidError} — manual entry still works.</div>
      )}
      {hidStatus === 'unsupported' && (
        <div className="text-xs text-slate-500">
          WebHID isn't available in this browser. Use Chrome or Edge over HTTPS, or just type the weight.
        </div>
      )}
    </div>
  );
}
